import { Injectable, NgZone, signal, computed, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import * as Sentry from '@sentry/angular';
import { environment } from '@environments/environment';
import { FacebookSdkApiService } from './api/facebook-sdk-api.service';

declare const FB: any;

// `/me/accounts` est paginé et Graph n'honore PAS `limit=1000` sur cette edge :
// il plafonne la taille de lot et renvoie un curseur. On demande donc un lot
// raisonnable et on suit `paging.cursors.after`.
const PAGES_BATCH_SIZE = 100;
// Garde-fou : un curseur qui ne progresse pas ferait tourner la boucle
// indéfiniment. 50 lots = 5000 pages, très au-delà de tout portfolio réel.
const MAX_PAGE_HOPS = 50;

// Graph ne renvoie JAMAIS de réponse partielle : un seul champ dont la
// permission manque fait échouer TOUT l'appel — vécu avec `business` (qui exige
// `business_management`), `/me/accounts` renvoyait alors zéro page.
//
// D'où la séparation. REQUIRED ne dépend que de `pages_show_list` : sans elle il
// n'y a de toute façon rien à faire ici. OPTIONAL dépend de
// `pages_read_engagement`, une permission qui vit dans la configuration Business
// Login et peut donc disparaître sans que ce code bouge — c'est arrivé deux fois
// le 2026-08-27. Si elle tombe, on rejoue sans les stats et le sélecteur liste
// quand même les pages, au lieu de rendre une liste vide qui condamnerait toute
// l'association.
//
// `instagram_business_account` n'est PAS demandé : il exige `instagram_basic`,
// qui est en accès standard sur l'app — donc inutilisable pour un client sans
// rôle, App Review obligatoire. L'information est récupérée côté serveur par
// routine_api, au jeton de page.
const REQUIRED_PAGE_FIELDS = 'id,name,access_token,picture{url}';
const OPTIONAL_PAGE_FIELDS = 'fan_count,followers_count';

// Interfaces pour typer les données
export interface FacebookPage {
    id: string;
    name: string;
    access_token: string;
    /**
     * N'est PLUS alimenté par le SDK (2026-08-27) : lire le compte Instagram d'une
     * page exige `instagram_basic`, qui est en accès standard sur l'app — elle ne
     * fonctionnerait donc que pour un utilisateur ayant un rôle sur l'app, jamais
     * pour un client. Sans App Review, ce chemin est un cul-de-sac.
     *
     * L'information vient désormais du serveur : routine_api lit
     * `instagram_business_account` au jeton de page et l'écrit dans
     * `child_bm_page.instagram_account_id`, que `GET /form/.../meta-pages`
     * renvoie. Le champ reste déclaré pour le jour où la permission passerait en
     * accès avancé — il suffirait alors de remettre `instagram_business_account`
     * dans OPTIONAL_PAGE_FIELDS.
     */
    instagram_account_id?: string;
    fan_count?: number;
    followers_count?: number;
    picture_url?: string;
    business?: any;
}

export interface FacebookUser {
    accessToken: string;
    longAccessToken?: string;
}

export interface FacebookPost {
    id: string;
    message?: string;
    created_time: string;
    full_picture?: string;
    shortMessage?: string;
    permalink_url?: string;
    properties?: { name: string; text: string }[];
    width?: number;
    height?: number;
    promotable_id?: string;
    is_eligible_for_promotion?: boolean;
}

export interface InstagramPost {
    id: string;
    caption?: string;
    media_type: 'IMAGE' | 'VIDEO' | 'CAROUSEL_ALBUM';
    media_url?: string;
    thumbnail_url?: string;
    permalink?: string;
    timestamp: string;
    width?: number;
    height?: number;
}

export interface FacebookPageFeed {
    id: string;
    name: string;
    posts: FacebookPost[];
}

export interface FacebookLoginResponse {
    accessToken?: string; // mode classique
    code?: string; // mode Business Login
}

@Injectable({
    providedIn: 'root',
})
export class FacebookService {
    private api = inject(FacebookSdkApiService);

    private readonly FB_SESSION_KEY = `fbssls_${environment.facebook_app_id}`;
    private readonly DEFAULT_SCOPES =
        'ads_management, pages_read_engagement, pages_manage_engagement, pages_manage_metadata, pages_show_list, public_profile, instagram_basic';

    // ========== SIGNALS - ÉTATS ==========

    // État de la connexion
    isAuthenticated = signal<boolean>(false);
    currentUser = signal<FacebookUser | null>(null);

    // Cache des pages et posts
    userPages = signal<FacebookPage[]>([]);
    pageFeeds = signal<FacebookPageFeed[]>([]);

    // États de chargement
    isLoadingAuth = signal<boolean>(false);
    isLoadingPages = signal<boolean>(false);
    isLoadingPosts = signal<boolean>(false);
    isSDKReady = signal<boolean>(false);

    // Vrai quand la liste renvoyée par getPages() est INCOMPLÈTE : échec en cours
    // de pagination, ou borne MAX_PAGE_HOPS atteinte. Tant qu'on n'affichait pas
    // la liste, une troncature passait inaperçue ; le sélecteur de pages la
    // montre, donc l'UI doit pouvoir dire « cette liste est partielle » au lieu
    // de la présenter comme complète — sinon l'utilisateur ne voit pas sa page et
    // conclut à un bug.
    pagesLoadIncomplete = signal<boolean>(false);

    // Vrai quand les statistiques de page (fans, followers) ont dû être
    // abandonnées pour que l'appel passe. Sans ce drapeau, l'UI afficherait
    // « 0 fans » partout — un chiffre faux valant moins que pas de chiffre.
    pageStatsUnavailable = signal<boolean>(false);

    // Computed signals
    hasPages = computed(() => this.userPages().length > 0);
    hasLongToken = computed(() => !!this.currentUser()?.longAccessToken);
    isReady = computed(() => this.isAuthenticated() && !this.isLoadingAuth());

    constructor(private ngZone: NgZone) {
        this.checkExistingSession();
    }

    markSDKReady(): void {
        this.ngZone.run(() => this.isSDKReady.set(true));
    }

    // ========== AUTHENTIFICATION ==========

    /**
     * Connexion Facebook avec gestion d'état automatique
     */
    async login(): Promise<FacebookUser> {
        this.isLoadingAuth.set(true);

        try {
            const existingToken = this.getStoredAccessToken();
            if (existingToken) {
                const user: FacebookUser = { accessToken: existingToken };
                this.setAuthenticatedUser(user);
                return user;
            }

            const loginResponse = await this.openLoginDialog();

            // Business Login → on a un `code` à échanger
            if (loginResponse.code) {
                const accessToken = await firstValueFrom(this.api.exchangeCodeForToken(loginResponse.code));
                if (!accessToken) throw new Error('Failed to exchange code for token');

                const user: FacebookUser = {
                    accessToken,
                };
                this.setAuthenticatedUser(user);
                return user;
            }

            // Fallback mode classique (si jamais)
            if (loginResponse.accessToken) {
                const user: FacebookUser = {
                    accessToken: loginResponse.accessToken,
                };
                this.setAuthenticatedUser(user);
                return user;
            }

            throw new Error('No token or code received from Facebook');
        } finally {
            this.isLoadingAuth.set(false);
        }
    }

    /**
     * Obtient et cache le long access token
     */
    async getLongAccessToken(forceRefresh: boolean = false): Promise<string | null> {
        const currentUser = this.currentUser();
        if (!currentUser) {
            console.warn('No user authenticated');
            return null;
        }

        // Si on a déjà le long token en cache et qu'on ne force pas le refresh
        if (currentUser.longAccessToken && !forceRefresh) {
            return currentUser.longAccessToken;
        }

        // Sinon, l'obtenir et le mettre en cache
        const longToken = await this.getUserLongAccessToken(currentUser.accessToken);

        if (longToken) {
            this.currentUser.update((user) =>
                user
                    ? {
                          ...user,
                          longAccessToken: longToken,
                      }
                    : null,
            );
        }

        return longToken;
    }

    /**
     * Récupère et cache les pages de l'utilisateur
     */
    async fetchAndCachePages(access_token: string, forceRefresh: boolean = false): Promise<FacebookPage[]> {
        // Si on a déjà les pages en cache et qu'on ne force pas le refresh
        if (this.userPages().length > 0 && !forceRefresh) {
            return this.userPages();
        }

        this.isLoadingPages.set(true);

        try {
            const pages = await this.getPages(access_token);
            this.userPages.set(pages);
            return pages;
        } finally {
            this.isLoadingPages.set(false);
        }
    }

    /**
     * Récupère et cache les posts des pages
     */
    async fetchAndCachePageFeeds(pages?: FacebookPage[], forceRefresh: boolean = false): Promise<FacebookPageFeed[]> {
        // Si on a déjà les feeds en cache et qu'on ne force pas le refresh
        if (this.pageFeeds().length > 0 && !forceRefresh) {
            return this.pageFeeds();
        }

        this.isLoadingPosts.set(true);

        try {
            // Utiliser les pages passées en paramètre ou celles en cache
            const pagesToUse = pages || this.userPages();

            if (pagesToUse.length === 0) {
                console.warn('No pages available to fetch feeds');
                return [];
            }

            const feeds = await this.getPagesFeed(pagesToUse);
            this.pageFeeds.set(feeds);
            return feeds;
        } finally {
            this.isLoadingPosts.set(false);
        }
    }

    // `loginAndFetchData()` enchaînait login → token long → pages en un seul
    // await. Supprimée : FormsComponent doit ouvrir le sélecteur ENTRE le login et
    // la récupération des pages, pour afficher un chargement au lieu d'un écran
    // figé. Deux chemins d'orchestration auraient fini par diverger — les
    // appelants composent `login()`, `getLongAccessToken()` et
    // `fetchAndCachePages()` directement.

    /**
     * Ouvre la modal de connexion Facebook
     */
    private openLoginDialog(): Promise<FacebookLoginResponse> {
        return new Promise((resolve, reject) => {
            FB.login(
                (response: any) => {
                    this.ngZone.run(() => {
                        if (response.authResponse) {
                            resolve({
                                // Business Login renvoie `code`, pas `accessToken`
                                code: response.authResponse.code,
                                accessToken: response.authResponse.accessToken, // en mode classique
                            });
                        } else {
                            reject(new Error('User cancelled login or did not fully authorize'));
                        }
                    });
                },
                {
                    config_id: environment.facebook_app_scope_config_id,
                    response_type: 'code',
                    override_default_response_type: true,
                },
            );
        });
    }

    /**
     * Récupère le token stocké dans sessionStorage
     */
    private getStoredAccessToken(): string | null {
        try {
            const sessionData = sessionStorage.getItem(this.FB_SESSION_KEY);
            if (sessionData) {
                const parsed = JSON.parse(sessionData);
                return parsed?.authResponse?.accessToken || null;
            }
        } catch (error) {
            Sentry.captureException(error, { extra: { step: 'fb-read-stored-token' } });
        }
        return null;
    }

    /**
     * Échange un token court contre un token long (60 jours)
     */
    async getUserLongAccessToken(shortAccessToken: string): Promise<string | null> {
        if (!shortAccessToken) {
            console.warn('Access token is null');
            return null;
        }

        try {
            return await firstValueFrom(this.api.getLongAccessToken(shortAccessToken));
        } catch {
            return null;
        }
    }

    /**
     * Efface les données de session Facebook
     */
    async clearSessionData(): Promise<void> {
        return new Promise((resolve) => {
            let isCleared = false;
            while (!isCleared) {
                sessionStorage.removeItem(this.FB_SESSION_KEY);
                isCleared = sessionStorage.getItem(this.FB_SESSION_KEY) === null;
            }
            resolve();
        });
    }

    /**
     * Déconnexion avec reset des états
     */
    async logout(): Promise<void> {
        return new Promise((resolve) => {
            FB.logout(() => {
                this.ngZone.run(async () => {
                    await this.clearSessionData();
                    this.resetAuthState();
                    resolve();
                });
            });
        });
    }

    // ========== PAGES ==========

    /**
     * Récupère TOUTES les pages accessibles au token (méthode bas niveau).
     *
     * La sélection faite sur l'écran de consentement Meta est une frontière
     * d'AUTORISATION, pas un choix métier : selon la configuration Business
     * Login, le token porte tout le portfolio et cette liste contient donc bien
     * plus que ce que l'utilisateur croit avoir coché. Le tri se fait dans notre
     * UI (voir MetaPagePickerComponent), pas ici.
     */
    async getPages(accessToken: string): Promise<FacebookPage[]> {
        if (!accessToken) {
            console.warn('Access token is null');
            return [];
        }

        this.pagesLoadIncomplete.set(false);
        this.pageStatsUnavailable.set(false);

        const collected: FacebookPage[] = [];
        let after: string | undefined;
        let includeOptional = true;
        let hop = 0;

        while (hop < MAX_PAGE_HOPS) {
            const fields = includeOptional ? `${REQUIRED_PAGE_FIELDS},${OPTIONAL_PAGE_FIELDS}` : REQUIRED_PAGE_FIELDS;
            const query =
                `/me/accounts?fields=${fields}&limit=${PAGES_BATCH_SIZE}` +
                (after ? `&after=${encodeURIComponent(after)}` : '') +
                `&access_token=${accessToken}`;

            const resp = await this.graphGet(query);

            // Rejeu UNIQUE, et seulement sur la toute première requête : un refus
            // de permission est déterministe et tombe d'emblée, alors qu'une
            // erreur en cours de pagination est transitoire (quota, réseau) et ne
            // se règle pas en retirant un champ. Ce rejeu ne consomme pas un cran
            // de MAX_PAGE_HOPS.
            if ((!resp || resp.error) && includeOptional && collected.length === 0) {
                console.warn(
                    '[FB getPages] Optional page stats fields refused, retrying without them:',
                    resp?.error?.message || 'unknown error',
                );
                includeOptional = false;
                this.pageStatsUnavailable.set(true);
                continue;
            }

            hop++;

            if (!resp || resp.error) {
                Sentry.captureException(new Error(resp?.error?.message || 'Failed to fetch Facebook pages'), {
                    extra: { step: 'fb-get-pages', fb_error_code: resp?.error?.code, hop, collected: collected.length },
                });
                // On garde ce qui a déjà été récupéré — mais on l'avoue.
                this.pagesLoadIncomplete.set(collected.length > 0);
                break;
            }

            const batch: any[] = Array.isArray(resp.data) ? resp.data : [];
            for (const page of batch) {
                if (page?.id) collected.push(this.toFacebookPage(page));
            }

            after = resp.paging?.cursors?.after;
            if (!resp.paging?.next || !after || batch.length === 0) break;

            // Sortie par la borne et non parce que Meta a dit « fini ».
            if (hop === MAX_PAGE_HOPS - 1) this.pagesLoadIncomplete.set(true);
        }

        if (collected.length === 0) {
            console.warn('User has no pages');
        }

        return collected;
    }

    /**
     * `FB.api` en promesse. Le callback du SDK s'exécute hors zone Angular : sans
     * `ngZone.run`, les signaux écrits dans la suite du flux ne déclencheraient
     * pas de détection de changement sur les composants OnPush.
     */
    private graphGet(query: string): Promise<any> {
        return new Promise((resolve) => {
            FB.api(query, (resp: any) => this.ngZone.run(() => resolve(resp)));
        });
    }

    private toFacebookPage(page: any): FacebookPage {
        return {
            id: page.id,
            name: page.name,
            access_token: page.access_token,
            fan_count: page.fan_count || 0,
            followers_count: page.followers_count || 0,
            picture_url: page.picture?.data?.url || undefined,
            business: page.business || undefined,
        };
    }

    /**
     * Récupère les posts Instagram d'un compte lié à une page
     */
    async getInstagramFeed(igUserId: string, accessToken: string): Promise<InstagramPost[]> {
        if (!igUserId || !accessToken) {
            console.warn('Instagram user ID or access token is null');
            return [];
        }

        return new Promise((resolve) => {
            FB.api(
                `/${igUserId}/media?fields=id,caption,media_type,media_url,permalink,timestamp&limit=30&access_token=${accessToken}`,
                (resp: any) => {
                    if (resp.error) {
                        Sentry.logger.error('Failed to fetch Instagram posts', {
                            ig_user_id: igUserId,
                            fb_error_code: resp.error.code,
                            fb_error_message: resp.error.message,
                        });
                        resolve([]);
                    } else {
                        resolve(resp.data || []);
                    }
                },
            );
        });
    }

    /**
     * Récupère les posts de plusieurs pages (méthode bas niveau)
     */
    async getPagesFeed(pageInfos: FacebookPage[]): Promise<FacebookPageFeed[]> {
        if (!Array.isArray(pageInfos) || pageInfos.length === 0) {
            console.warn('Page infos array is empty');
            return [];
        }

        const pagePromises = pageInfos.map((pageInfo) => {
            const { id, access_token, name } = pageInfo;

            if (!id || !access_token) {
                console.warn('Page ID or Token is null for page:', name);
                return Promise.resolve(null);
            }

            return new Promise<FacebookPageFeed | null>((resolve) => {
                try {
                    FB.api(
                        `${id}/posts?fields=id,message,created_time,full_picture,permalink_url,properties,attachments{media{image{width,height}}},promotable_id,is_eligible_for_promotion&limit=30&access_token=${access_token}`,
                        (resp: any) => {
                            if (resp.error) {
                                Sentry.logger.error('Failed to fetch page posts', {
                                    page_name: name,
                                    page_id: id,
                                    fb_error_code: resp.error.code,
                                    fb_error_message: resp.error.message,
                                });
                                resolve(null);
                            } else {
                                const posts: FacebookPost[] = (resp.data || []).map((post: any) => {
                                    const image = post.attachments?.data?.[0]?.media?.image;
                                    const resolvedId = post.promotable_id && post.promotable_id !== post.id ? post.promotable_id : post.id;
                                    return {
                                        ...post,
                                        id: resolvedId,
                                        width: image?.width,
                                        height: image?.height,
                                        is_eligible_for_promotion: post.is_eligible_for_promotion ?? false,
                                    };
                                });
                                resolve({ id, name, posts });
                            }
                        },
                    );
                } catch (error) {
                    Sentry.captureException(error, { extra: { step: 'fb-get-page-posts', page_name: name, page_id: id } });
                    resolve(null);
                }
            });
        });

        const results = await Promise.all(pagePromises);
        return results.filter((result): result is FacebookPageFeed => result !== null);
    }

    // ========== UTILITAIRES ==========

    /**
     * Vérifie si le SDK Facebook est chargé
     */
    isSDKLoaded(): boolean {
        return typeof FB !== 'undefined';
    }

    /**
     * Ajoute un shortMessage aux posts
     */
    formatPostsWithShortMessage(posts: FacebookPost[], maxLength: number = 20): FacebookPost[] {
        return posts.map((post) => ({
            ...post,
            shortMessage: post.message ? post.message.slice(0, maxLength) + (post.message.length > maxLength ? '...' : '') : post.message,
        }));
    }

    /**
     * Trouve une page spécifique par ID dans le cache
     */
    findPageById(pageId: string): FacebookPage | undefined {
        return this.userPages().find((page) => page.id === pageId);
    }

    /**
     * Trouve les posts d'une page spécifique dans le cache
     */
    findPageFeedById(pageId: string): FacebookPageFeed | undefined {
        return this.pageFeeds().find((feed) => feed.id === pageId);
    }

    // ========== MÉTHODES PRIVÉES ==========

    private setAuthenticatedUser(user: FacebookUser): void {
        this.currentUser.set(user);
        this.isAuthenticated.set(true);
    }

    private resetAuthState(): void {
        this.isAuthenticated.set(false);
        this.currentUser.set(null);
        this.userPages.set([]);
        this.pageFeeds.set([]);
    }

    private checkExistingSession(): void {
        const token = this.getStoredAccessToken();
        if (token) {
            this.setAuthenticatedUser({ accessToken: token });
        }
    }
}
